import React, { type ForwardedRef, forwardRef, type MouseEvent } from 'react';

import classnames from 'classnames';

import { type SizeType, useSize } from '../../features/config';
import { type ThemedColors } from '../../types/themed-colors';

import { type IconName } from './Icon.types';

import styles from './Icon.module.scss';

export type IconSpecificColor = 'base' | 'disabled' | 'link';

export type IconColor = ThemedColors | IconSpecificColor;

// we don't define a default font size, so it can be inherited from the parent by default
// as it is the most common use case.
const DEFAULT_ICON_SIZE = null;

export type IconProps = {
    name: IconName;
    /**
     * @deprecated Wrap the icon in a Button component and add an onClick handler to the Button component instead.
     */
    onClick?: (event: MouseEvent) => void;
    className?: string;
    btnClassName?: string;
    color?: IconColor;
    size?: SizeType;
    customSize?: string | number;
} & React.HTMLAttributes<HTMLElement>;

const IconComponent = (
    {
        name,
        onClick,
        className,
        btnClassName,
        color,
        size: sizeProp,
        customSize,
        ...rest
    }: IconProps,
    ref: ForwardedRef<HTMLElement>
) => {
    const size = useSize(sizeProp, DEFAULT_ICON_SIZE, 'Icon');

    const customSizeStyle = customSize ? { fontSize: customSize } : {};

    const icon = (
        <i
            ref={ref}
            style={{ ...customSizeStyle }}
            className={classnames(
                styles.icon,
                name,
                className,
                color && styles[color],
                size && styles[size]
            )}
            {...rest}
        />
    );

    if (onClick) {
        return (
            <button
                ref={ref as ForwardedRef<HTMLButtonElement>}
                type="button"
                className={classnames(styles.iconButton, btnClassName)}
                onClick={onClick}
            >
                {icon}
            </button>
        );
    }

    return icon;
};

export const Icon = forwardRef<HTMLElement, IconProps>(IconComponent);

Icon.displayName = 'Icon';
